import type { ExportJob } from '../api/contracts'
import { describeExportProgress, isExportActive } from './exportJobs'

const DAY_MS = 24 * 60 * 60_000
const countFormat = new Intl.NumberFormat()
const timestampFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

/** How long ago, in one unit, rounded down: "just now", "43m ago", "5h ago", "3d ago". */
export function relativeAge(iso: string, now: number): string {
  const minutes = Math.floor(Math.max(0, now - Date.parse(iso)) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/** A running export's progress in a few characters: "Waiting", "Starting", "45%", "1,200 rows". */
export function shortProgress(job: ExportJob): string {
  if (job.status === 'queued') return 'Waiting'
  if (job.rows_total) {
    return `${Math.min(99, Math.floor((100 * job.rows_written) / job.rows_total))}%`
  }
  return job.rows_written ? `${countFormat.format(job.rows_written)} rows` : 'Starting'
}

/** "Expires in 5h" (or "in 40m") within a day of expiry; null before that. */
export function expiresSoon(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null
  const left = Date.parse(iso) - now
  if (left <= 0 || left > DAY_MS) return null
  const minutes = Math.max(1, Math.ceil(left / 60_000))
  return minutes < 60 ? `Expires in ${minutes}m` : `Expires in ${Math.floor(minutes / 60)}h`
}

/** The exact time, for a row's tooltip. */
export function fullTimestamp(iso: string): string {
  return timestampFormat.format(new Date(iso))
}

/** A menu button's dot: amber while any export runs, else blue for one ready and unseen. */
export type ExportMenuDot = 'running' | 'ready' | null

export function exportMenuDot(jobs: ExportJob[]): ExportMenuDot {
  if (jobs.some(isExportActive)) return 'running'
  if (jobs.some((job) => job.status === 'done' && !job.seen_at)) return 'ready'
  return null
}

/** The button's tooltip while exports run: what and how far. */
export function runningSummary(jobs: ExportJob[]): string | null {
  const running = jobs.filter(isExportActive)
  if (running.length === 0) return null
  if (running.length === 1) {
    return `Exporting ${running[0].file_name}: ${describeExportProgress(running[0])}`
  }
  return `Exporting ${countFormat.format(running.length)} files: ${running
    .map((job) => `${job.file_name} (${describeExportProgress(job)})`)
    .join(', ')}`
}

/** The menu button's tooltip: what's running and how far, or that a file is ready. */
export function exportMenuTooltip(base: string, dot: ExportMenuDot, running: string | null) {
  if (dot === 'running' && running) return running
  if (dot === 'ready') return `${base}: an export is ready`
  return base
}
